import React, { useState, useRef } from "react";
import {
  motion,
  AnimatePresence,
  useScroll,
  useTransform,
} from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import ScrollReveal from "./ScrollReveal";
import Footer from "./Footer";
import { ArrowLeft } from "lucide-react";

const DataSecurity = () => {
  const navigate = useNavigate();
  // Reference for the timeline section to track scroll progress
  const timelineRef = useRef(null);

  // Captures scroll progress specifically within this container
  const { scrollYProgress } = useScroll({
    target: timelineRef,
    offset: ["start center", "end center"],
  });

  // Transforms the scroll progress into a height scale percentage (from 0% to 100%)
  const scaleY = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);

  const tabsData = [
    {
      id: "tab1",
      title: "Vendor-Neutral Advisory",
      contentTitle: "Vendor-Neutral Advisory",
      description:
        "Independent guidance focused on business and security outcomes rather than product-driven engagements.",
    },
    {
      id: "tab2",
      title: "Business-Aligned Transformation",
      contentTitle: "Business-Aligned Transformation",
      description:
        "Security strategies aligned to operational priorities, user experience, scalability, and risk reduction.",
    },
    {
      id: "tab3",
      title: "Deep Technical Expertise",
      contentTitle: "Deep Technical Expertise",
      description:
        "Extensive experience across cloud security, SASE, Zero Trust, identity, AI security, and modern enterprise architectures.",
    },
    {
      id: "tab4",
      title: "Outcome-Driven Execution",
      contentTitle: "Outcome-Driven Execution",
      description:
        "Structured engagements designed to accelerate transformation while minimizing operational disruption.",
    },
    {
      id: "tab5",
      title: "Modern Security Vision",
      contentTitle: "Modern Security Vision",
      description:
        "Helping organizations build resilient security architectures designed for cloud-first, AI-driven, and hybrid environments.",
    },
  ];

  const [activeTab, setActiveTab] = useState("tab1");

  const timelineData = [
    {
      title: "Business & Data Governance Alignment",
      description:
        "Align security, compliance, legal, privacy, IT, and business stakeholders around data protection objectives, governance expectations, regulatory requirements, and operational priorities.",
    },
    {
      title: "Data Discovery & Visibility Assessment",
      description:
        "Identify sensitive, regulated, business-critical, and high-risk data across endpoints, cloud environments, SaaS applications, collaboration platforms, AI systems, and on-premises infrastructure.",
    },
    {
      title: "Data Classification & Risk Analysis",
      description:
        "Define data classification models and assess risks related to data exposure, oversharing, unauthorized access, shadow data, insider threats, and insecure data handling practices.",
    },
    {
      title: "Access Control & Data Protection Assessment",
      description:
        "Evaluate identity controls, data access policies, encryption standards, DLP capabilities, sharing permissions, retention controls, and monitoring mechanisms protecting sensitive data.",
    },
    {
      title: "Cloud, SaaS & AI Data Security Assessment",
      description:
        "Assess how sensitive data is accessed, processed, shared, and exposed across cloud platforms, SaaS applications, generative AI tools, and third-party services.",
    },
    {
      title: "Data Security Architecture & Policy Design",
      description:
        "Design governance frameworks, protection strategies, DLP policies, access control models, data lifecycle controls, and monitoring capabilities aligned to business and compliance requirements.",
    },
    {
      title: "Deployment, Visibility & Continuous Optimization",
      description:
        "Deploy and operationalize data security capabilities using structured implementation methodologies. Enable centralized visibility, policy enforcement, governance workflows, monitoring, and continuous optimization of data security posture.",
    },
  ];

  return (
    <div className="bg-[#011916] text-white min-h-screen font-sans overflow-x-hidden selection:bg-[#048649] selection:text-white">
      <button
        onClick={() => navigate(-1)}
        className="absolute top-20 left-8 z-50 flex items-center justify-center w-10 h-10 rounded-full bg-black/20 hover:bg-black/40 text-white/70 hover:text-[#24ff81] transition-all duration-300"
      >
        <ArrowLeft size={20} />
      </button>

      {/* SECTION 1: TOP HERO HEADER SECTION */}
      <ScrollReveal>
        <div className="pt-32 pb-16 px-6 max-w-[1600px] mx-auto">
          <section className="relative bg-[#022722] rounded-[3rem] p-8 md:p-10 lg:p-12 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
            {/* LEFT CONTENT */}
            <div className="md:col-span-6">
              <h1 className="text-2xl md:text-4xl font-black text-white leading-tight mb-4 tracking-tight">
                Data Security
              </h1>

              <h4 className="text-white text-lg md:text-xl font-extrabold mb-5 tracking-wide uppercase">
                Protecting Sensitive Data Across Modern Hybrid and Cloud
                Environments
              </h4>

              <p className="text-gray-300 text-base md:text-lg leading-[1.8] mb-10 max-w-2xl">
                Data is one of the most valuable assets in the modern enterprise
                and one of the most targeted. As organizations adopt cloud
                platforms, SaaS applications, AI technologies, hybrid work
                models, and distributed environments, sensitive data
                increasingly moves beyond traditional security boundaries
                creating significant visibility, governance, and protection
                challenges.
                <br />
                <br />
                Synthera Security’s Data Security Service helps organizations
                discover, classify, govern, and protect sensitive data across
                users, endpoints, cloud platforms, SaaS applications, AI
                ecosystems, and hybrid environments. Through a strategic,
                risk-driven approach, we help enterprises strengthen data
                protection, reduce exposure risks, improve regulatory readiness,
                and maintain visibility and control over critical business data.
              </p>

              <Link
                to="/contact"
                className="bg-[#048649] hover:bg-[#036c3a] text-white font-bold text-sm md:text-base px-7 py-3 rounded-md transition-all duration-200 w-fit no-underline shadow-lg shadow-green-950/20"
              >
                Work with Us
              </Link>
            </div>

            {/* RIGHT IMAGE */}
            <div className="md:col-span-6 flex justify-center items-center w-full">
              <div className="w-[85%] rounded-2xl overflow-hidden shadow-2xl shadow-green-950/10">
                <img
                  src="  https://res.cloudinary.com/dpn3bkjba/image/upload/f_auto,q_auto:good,w_1200/v1780331593/ChatGPT_Image_Jun_1_2026_07_53_35_PM_1_aywfmy.png"
                  alt="Data Security Visual"
                  className="w-full h-[600px] object-cover rounded-2xl"
                />
              </div>
            </div>
          </section>
        </div>
      </ScrollReveal>

      {/* SECTION 2: EXPERIENCE THE DIFFERENCE (TAB SELECTOR) */}
      <ScrollReveal>
        <section className="relative bg-[#011916] py-20 px-6 max-w-6xl mx-auto">
          <div className="text-center md:text-left mb-12">
            <span className="text-gray-300 text-xs font-bold uppercase tracking-wider block mb-1">
              Why Choose Synthera Security?
            </span>
            <h2 className="text-lg md:text-2xl font-black text-white uppercase tracking-tight">
              Experience the Difference
            </h2>
            <p className="text-gray-300 text-xs md:text-sm mt-2">
              Synthera Security combines deep cybersecurity expertise with
              practical data protection experience to help organizations
              modernize data security for today’s cloud-first and AI-driven
              enterprise.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-start">
            {/* Left Button Controls Menu */}
            <div className="md:col-span-5 flex flex-col gap-2">
              {tabsData.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full text-left px-5 py-3.5 rounded-lg border text-xs md:text-sm font-bold transition-all duration-200 ${
                    activeTab === tab.id
                      ? "bg-[#04433c] text-emerald-300 border-emerald-500/30 shadow-md"
                      : "bg-[#021f1b] text-gray-400 border-white/5 hover:bg-[#022722] hover:text-white"
                  }`}
                >
                  {tab.title}
                </button>
              ))}
            </div>

            {/* Right Display Presentation Panel */}
            <div className="md:col-span-7 h-full min-h-[200px]">
              <div className="bg-[#021f1b] border border-white/5 rounded-xl p-8 h-full flex flex-col justify-center relative min-h-[220px]">
                <AnimatePresence mode="wait">
                  {tabsData.map(
                    (tab) =>
                      tab.id === activeTab && (
                        <motion.div
                          key={tab.id}
                          initial={{ opacity: 0, x: 8 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: -8 }}
                          transition={{ duration: 0.15 }}
                        >
                          <h3 className="text-lg md:text-xl font-bold text-white mb-3">
                            {tab.contentTitle}
                          </h3>
                          <p className="text-gray-300 text-xs md:text-sm leading-relaxed">
                            {tab.description}
                          </p>
                        </motion.div>
                      ),
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>
        </section>
      </ScrollReveal>

      {/* SECTION 3: MID-PAGE EXPOSITION */}
      <ScrollReveal>
        <section className="bg-[#011916] py-16 px-6 text-center border-t border-white/5">
          <div className="max-w-3xl mx-auto">
            <span className="text-gray-300 text-xs font-semibold uppercase tracking-wider block mb-1">
              Our Approach
            </span>
            <h2 className="text-lg md:text-2xl font-black text-white tracking-tight uppercase mb-4">
              Beyond Traditional Data Protection
            </h2>
            <p className="text-gray-300 text-xs md:text-sm leading-relaxed">
              Modern enterprise data no longer resides within a single network
              boundary. Sensitive data continuously moves across cloud
              platforms, SaaS applications, endpoints, collaboration tools, AI
              ecosystems, and hybrid environments creating significant
              visibility, governance, and protection challenges. Effective data
              security requires more than isolated DLP controls or
              perimeter-based protections. Synthera Security delivers a holistic
              data security approach focused on data discovery, classification,
              governance, access control, monitoring, and protection across the
              entire data lifecycle. We help organizations secure sensitive data
              across users, applications, cloud services, and AI-driven
              workflows while improving visibility, reducing exposure risk, and
              enabling secure digital transformation.
            </p>
          </div>
        </section>
      </ScrollReveal>

      {/* SECTION 4: DYNAMIC TIMELINE / METHODOLOGY */}
      <ScrollReveal>
        <section
          ref={timelineRef}
          className="bg-[#031513] pb-32 pt-6 px-6 max-w-4xl mx-auto relative rounded-2xl"
        >
          <div className="mb-16 text-center md:text-left">
            <span className="text-gray-300 text-xs font-semibold uppercase tracking-wider block mb-1">
              Synthera Methodology
            </span>
            <h2 className="text-lg md:text-2xl font-black text-white uppercase tracking-tight">
              Assessment & Implementation
            </h2>
          </div>

          {/* Timeline Wrapper Grid Layout */}
          <div className="relative pl-6 md:pl-10">
            {/* Background Base Muted Track Line */}
            <div className="absolute left-1.5 md:left-3 top-2 bottom-2 w-[1px] bg-[#021f1b]" />

            {/* DYNAMIC SCROLLING GREEN ACCENT LINE STEM */}
            <motion.div
              style={{ height: scaleY }}
              className="absolute left-1.5 md:left-3 top-2 w-[1px] bg-emerald-500 origin-top z-10 shadow-[0_0_8px_rgba(16,185,129,0.5)]"
            />

            <div className="flex flex-col gap-12 relative">
              {timelineData.map((item, index) => (
                <div key={index} className="w-full relative group">
                  {/* Dynamic Glowing Green Circle Node Marker */}
                  <motion.div
                    initial={{ scale: 0.6, backgroundColor: "#021f1b" }}
                    whileInView={{ scale: 1, backgroundColor: "#048649" }}
                    viewport={{ once: false, amount: "even" }}
                    transition={{ duration: 0.3 }}
                    className="absolute -left-[23px] md:-left-[35px] w-2.5 h-2.5 top-1.5 rounded-full z-20 ring-4 ring-[#032d28]"
                  />

                  {/* Dynamic Text Group revealing on Scroll viewport arrival */}
                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: false, amount: 0.2 }}
                    transition={{ duration: 0.4 }}
                  >
                    <h3 className="text-base md:text-lg font-bold text-white mb-2 tracking-tight transition-colors duration-300 group-hover:text-emerald-400">
                      {item.title}
                    </h3>
                    <p className="text-gray-300 text-xs md:text-sm leading-relaxed max-w-3xl font-light">
                      {item.description}
                    </p>
                  </motion.div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </ScrollReveal>
      <Footer />
    </div>
  );
};

export default DataSecurity;
